import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Handle,
  Position
} from 'reactflow';
import { toPng } from 'html-to-image';
import 'reactflow/dist/style.css';
import { getHistory } from '../utils/analysisHistory';
import { apiService } from '../services/api';



// Custom Node Components
const CentralNode = ({ data }) => {
  return (
    <div style={{ position: 'relative' }}>
      <Handle type="source" position={Position.Left} id="left" style={{ background: '#555' }} />
      {data.label}
      <Handle type="source" position={Position.Right} id="right" style={{ background: '#555' }} />
    </div>
  );
};

const LeftNode = ({ data }) => {
  return (
    <div style={{ position: 'relative' }}>
      <Handle type="target" position={Position.Right} style={{ background: '#555' }} />
      {data.label}
      <Handle type="source" position={Position.Left} style={{ background: '#555' }} />
    </div>
  );
};

const RightNode = ({ data }) => {
  return (
    <div style={{ position: 'relative' }}>
      <Handle type="target" position={Position.Left} style={{ background: '#555' }} />
      {data.label}
      <Handle type="source" position={Position.Right} style={{ background: '#555' }} />
    </div>
  );
};

const LeftLeafNode = ({ data }) => {
  return (
    <div style={{ position: 'relative' }}>
      <Handle type="target" position={Position.Right} style={{ background: '#555' }} />
      {data.label}
    </div>
  );
};

const RightLeafNode = ({ data }) => {
  return (
    <div style={{ position: 'relative' }}>
      <Handle type="target" position={Position.Left} style={{ background: '#555' }} />
      {data.label}
    </div>
  );
};

const nodeTypes = {
  central: CentralNode,
  left: LeftNode,
  right: RightNode,
  leftLeaf: LeftLeafNode,
  rightLeaf: RightLeafNode
};

const MindMapViewer = ({ analysisResult }) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [availableAnalyses, setAvailableAnalyses] = useState([]);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);
  const [currentAnalysis, setCurrentAnalysis] = useState(analysisResult);
  const [isDownloadingSummary, setIsDownloadingSummary] = useState(false);
  const [isGeneratingMindMap, setIsGeneratingMindMap] = useState(false);
  const [manualUrl, setManualUrl] = useState('');
  const [manualStatus, setManualStatus] = useState({ error: '', success: '' });
  const [visibleNodeCount, setVisibleNodeCount] = useState(0);
  const mindMapWrapperRef = useRef(null);

  // Load analysis history
  useEffect(() => {
    const history = getHistory();
    const analyses = [];

    // Add current analysis if available
    if (analysisResult) {
      analyses.push({
        id: 'current',
        videoId: analysisResult.videoId,
        title: analysisResult.videoTitle || 'Latest Analysis',
        thumbnail: analysisResult.thumbnail,
        analyzedAt: new Date().toISOString()
      });
      setSelectedAnalysisId('current');
      setCurrentAnalysis(analysisResult);
    }

    // Add history analyses
    history.forEach((item) => {
      analyses.push({
        id: item.id,
        videoId: item.videoId,
        title: item.videoTitle || 'Unknown Video',
        thumbnail: item.thumbnail,
        analyzedAt: item.analyzedAt,
        analysis: item
      });
    });

    setAvailableAnalyses(analyses);
  }, [analysisResult]);

  // Handle analysis selection change
  const handleAnalysisChange = (e) => {
    const selectedId = e.target.value;
    setSelectedAnalysisId(selectedId);

    if (selectedId === 'current') {
      setCurrentAnalysis(analysisResult);
    } else {
      const selected = availableAnalyses.find(a => a.id === selectedId);
      if (selected?.analysis) {
        setCurrentAnalysis(selected.analysis);
      }
    }
  };

  const buildYoutubeUrlFromAnalysis = (analysis) => {
    if (!analysis) return null;
    if (analysis.youtubeUrl) return analysis.youtubeUrl;
    const videoId = analysis.videoId ||
      analysis.analysis?.video_metadata?.videoId ||
      analysis.analysis?.video_metadata?.video_id ||
      analysis.analysis?.video_metadata?.id;
    if (videoId) {
      return `https://www.youtube.com/watch?v=${videoId}`;
    }
    return null;
  };

  const applyMindMapResult = (payload, sourceId = 'manual') => {
    if (!payload?.mind_map) return;
    const entryId = payload.video_id ? `${sourceId}-${payload.video_id}` : `${sourceId}-${Date.now()}`;
    const derivedTitle = payload.video_title || currentAnalysis?.videoTitle || 'Generated Mind Map';
    const derivedThumbnail = payload.thumbnail || currentAnalysis?.thumbnail || currentAnalysis?.analysis?.video_metadata?.thumbnail;
    const newAnalysis = {
      id: entryId,
      videoId: payload.video_id || currentAnalysis?.videoId,
      videoTitle: derivedTitle,
      thumbnail: derivedThumbnail,
      youtubeUrl: payload.youtube_url || buildYoutubeUrlFromAnalysis(currentAnalysis),
      analysis: {
        ...(currentAnalysis?.analysis || {}),
        mind_map: payload.mind_map,
        mindmap_summary: payload.mindmap_summary,
        video_metadata: {
          ...(currentAnalysis?.analysis?.video_metadata || {}),
          title: derivedTitle,
          videoId: payload.video_id || currentAnalysis?.analysis?.video_metadata?.videoId,
          videoTitle: derivedTitle,
          channel: payload.channel_name || currentAnalysis?.analysis?.video_metadata?.channel,
          channelTitle: payload.channel_name || currentAnalysis?.analysis?.video_metadata?.channelTitle,
          thumbnail: derivedThumbnail
        }
      }
    };

    setCurrentAnalysis(newAnalysis);
    setSelectedAnalysisId(entryId);
    setAvailableAnalyses((prev) => {
      const filtered = prev.filter((item) => item.id !== entryId);
      const entry = {
        id: entryId,
        videoId: newAnalysis.videoId,
        title: derivedTitle,
        thumbnail: derivedThumbnail,
        analysis: newAnalysis
      };
      return [entry, ...filtered];
    });
  };

  const generateMindMapFromUrl = async (youtubeUrl, source = 'manual') => {
    if (!youtubeUrl) {
      setManualStatus({ error: 'Please enter a valid YouTube URL.', success: '' });
      return;
    }
    setIsGeneratingMindMap(true);
    if (source === 'manual') {
      setManualStatus({ error: '', success: '' });
    }
    try {
      const response = await apiService.generateMindMap(youtubeUrl.trim());
      if (response?.success && response?.data) {
        applyMindMapResult(response.data, source);
        if (source === 'manual') {
          setManualStatus({ error: '', success: 'Mind map generated successfully.' });
        }
      } else {
        throw new Error('Failed to generate mind map. Please try again.');
      }
    } catch (error) {
      const message = error?.response?.data?.detail || error?.message || 'Unable to generate mind map.';
      setManualStatus({ error: message, success: '' });
    } finally {
      setIsGeneratingMindMap(false);
    }
  };

  const summaryText = useMemo(() => {
    if (!currentAnalysis) return '';
    return currentAnalysis.analysis?.mindmap_summary || '';
  }, [currentAnalysis]);

  const notesSections = useMemo(() => {
    if (!summaryText) return [];
    const lines = summaryText.split('\n');
    const timestampRegex = /^\s*\[(\d{1,2}:\d{2}(?::\d{2})?)\s*(?:->|to)\s*(\d{1,2}:\d{2}(?::\d{2})?)\]\s*$/i;
    const sections = [];
    let currentSection = null;
    let inInsightsBlock = false;

    const pushSection = () => {
      if (currentSection) {
        currentSection.bullets = currentSection.bullets.filter(Boolean);
        sections.push(currentSection);
      }
      currentSection = null;
      inInsightsBlock = false;
    };

    const ensureSection = (timestampLabel = 'Overview') => {
      if (!currentSection) {
        currentSection = {
          timestamp: timestampLabel,
          title: '',
          theory: '',
          bullets: []
        };
      }
    };

    lines.forEach((rawLine) => {
      const line = rawLine.trim();
      if (!line) return;

      const tsMatch = line.match(timestampRegex);
      if (tsMatch) {
        pushSection();
        currentSection = {
          timestamp: `[${tsMatch[1]} -> ${tsMatch[2]}]`,
          title: '',
          theory: '',
          bullets: []
        };
        return;
      }

      ensureSection();

      if (/^overview:/i.test(line)) {
        currentSection.title = line.replace(/^overview:\s*/i, '').trim();
        return;
      }

      if (/^theory:/i.test(line)) {
        currentSection.theory = line.replace(/^theory:\s*/i, '').trim();
        inInsightsBlock = false;
        return;
      }

      if (/^insights:/i.test(line)) {
        inInsightsBlock = true;
        return;
      }

      if (/^[-•]/.test(line)) {
        currentSection.bullets.push(line.replace(/^[-•]\s*/, '').trim());
        return;
      }

      if (inInsightsBlock) {
        currentSection.bullets.push(line);
      } else if (!currentSection.theory) {
        currentSection.theory = line;
      } else {
        currentSection.bullets.push(line);
      }
    });

    pushSection();

    return sections.filter(section => section.title || section.theory || section.bullets.length > 0);
  }, [summaryText]);

  const videoTitle = useMemo(() => {
    if (!currentAnalysis) return 'Video Summary';
    return (
      currentAnalysis.videoTitle ||
      currentAnalysis.analysis?.video_metadata?.title ||
      currentAnalysis.analysis?.video_metadata?.videoTitle ||
      'Video Summary'
    );
  }, [currentAnalysis]);

  const loadJsPDF = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        reject(new Error('Window is not available'));
        return;
      }

      if (window.jspdf?.jsPDF) {
        resolve(window.jspdf.jsPDF);
        return;
      }

      const existingScript = document.getElementById('jspdf-cdn');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          if (window.jspdf?.jsPDF) {
            resolve(window.jspdf.jsPDF);
          } else {
            reject(new Error('jsPDF failed to load.'));
          }
        });
        existingScript.addEventListener('error', () => reject(new Error('Failed to load jsPDF.')));
        return;
      }

      const script = document.createElement('script');
      script.id = 'jspdf-cdn';
      script.src = 'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js';
      script.onload = () => {
        if (window.jspdf?.jsPDF) {
          resolve(window.jspdf.jsPDF);
        } else {
          reject(new Error('jsPDF failed to load.'));
        }
      };
      script.onerror = () => reject(new Error('Failed to load jsPDF.'));
      document.body.appendChild(script);
    });
  }, []);

  const loadHtml2Canvas = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        reject(new Error('Window is not available'));
        return;
      }

      if (window.html2canvas) {
        resolve(window.html2canvas);
        return;
      }

      const existingScript = document.getElementById('html2canvas-cdn');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          if (window.html2canvas) {
            resolve(window.html2canvas);
          } else {
            reject(new Error('html2canvas failed to load.'));
          }
        });
        existingScript.addEventListener('error', () => reject(new Error('Failed to load html2canvas.')));
        return;
      }

      const script = document.createElement('script');
      script.id = 'html2canvas-cdn';
      script.src = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js';
      script.onload = () => {
        if (window.html2canvas) {
          resolve(window.html2canvas);
        } else {
          reject(new Error('html2canvas failed to load.'));
        }
      };
      script.onerror = () => reject(new Error('Failed to load html2canvas.'));
      document.body.appendChild(script);
    });
  }, []);

  const handleDownloadSummary = useCallback(async () => {
    if (!summaryText) return;
    try {
      setIsDownloadingSummary(true);
      const [jsPDF, html2canvas] = await Promise.all([loadJsPDF(), loadHtml2Canvas()]);
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4'
      });

      const margin = 40;
      const maxWidth = 515;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const headerHeight = 50;
      const applyPageBackground = () => {
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
      };

      const renderHeader = (label = 'Gradi Summary and Mind Map') => {
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, pageWidth, headerHeight, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor('#000000');
        doc.text(label, margin, 35);
      };

      applyPageBackground();
      renderHeader();

      let cursorY = headerHeight + 30;

      const ensureSpace = (heightNeeded = 18) => {
        if (cursorY + heightNeeded > pageHeight - margin) {
          doc.addPage();
          applyPageBackground();
          renderHeader('Gradi Summary and Mind Map (continued)');
          cursorY = headerHeight + 30;
        }
      };

      doc.setFontSize(15);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor('#000000');
      doc.text('Video Summary', margin, cursorY);
      cursorY += 22;

      // Parse summary text into sections
      const timestampRegex = /^\s*\[\d{1,2}:\d{2}(?::\d{2})?\s*[→-]\s*\d{1,2}:\d{2}(?::\d{2})?\]/;
      const parsedSections = [];
      let currentSection = null;

      const lines = summaryText.split('\n');
      let i = 0;

      while (i < lines.length) {
        const rawLine = lines[i].replace(/→/g, '->').trim();

        if (!rawLine) {
          i++;
          continue;
        }

        // Check if line is a timestamp
        if (timestampRegex.test(rawLine)) {
          // Save previous section
          if (currentSection && (currentSection.heading || currentSection.bullets.length > 0)) {
            parsedSections.push(currentSection);
          }

          // Start new section
          currentSection = {
            timestamp: rawLine.replace(/\[|\]/g, '').replace(/\s*→\s*/, ' -> '),
            heading: '',
            bullets: []
          };
          i++;
          continue;
        }

        // If no current section yet, create one with default timestamp
        if (!currentSection) {
          currentSection = { timestamp: '', heading: '', bullets: [] };
        }

        // Check if this line is a heading (contains "Overview:" or is a short line ending with colon)
        const isHeading = !currentSection.heading && (
          rawLine.toLowerCase().startsWith('overview:') ||
          (rawLine.length < 120 && rawLine.endsWith(':')) ||
          (rawLine.length < 100 && rawLine.length > 10 && !rawLine.startsWith('•') && !rawLine.startsWith('-') && !rawLine.startsWith('*'))
        );

        if (isHeading) {
          // Extract heading, keep "Overview:" prefix if present, add if not
          let heading = rawLine.trim();
          if (!heading.toLowerCase().startsWith('overview:')) {
            heading = heading.replace(/:\s*$/, ''); // Remove trailing colon
            heading = `Overview: ${heading}`;
          }
          currentSection.heading = heading;
          i++;
          continue;
        }

        // Add as bullet point
        const cleaned = rawLine.replace(/^\s*[-•*]\s*/, '').trim();
        if (cleaned) {
          currentSection.bullets.push(cleaned);
        }

        i++;
      }

      // Save last section
      if (currentSection && (currentSection.heading || currentSection.bullets.length > 0)) {
        parsedSections.push(currentSection);
      }

      // Render sections
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11.5);
      doc.setTextColor('#000000');

      parsedSections.forEach((section, idx) => {
        // Calculate approximate height needed
        let sectionHeight = 35; // Base height for timestamp and heading
        if (section.heading) {
          const headingLines = doc.splitTextToSize(section.heading, maxWidth);
          sectionHeight += headingLines.length * 14;
        }
        section.bullets.forEach(bullet => {
          const bulletLines = doc.splitTextToSize(`• ${bullet}`, maxWidth - 18);
          sectionHeight += bulletLines.length * 14;
        });
        sectionHeight += 10; // Spacing

        ensureSpace(sectionHeight);

        // Render timestamp
        if (section.timestamp) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(11);
          doc.setTextColor('#000000');
          // Ensure timestamp format is [MM:SS -> MM:SS]
          let timestampText = section.timestamp.replace(/\[|\]/g, '').replace(/\s*→\s*/, ' -> ').replace(/\s*-\s*/, ' -> ');
          if (!timestampText.includes('->')) {
            timestampText = timestampText.replace(/\s*-\s*/, ' -> ');
          }
          doc.text(`[${timestampText}]`, margin, cursorY);
          cursorY += 16;
        }

        // Render heading/title with "Overview:" prefix
        if (section.heading) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(12);
          doc.setTextColor('#000000');
          // Ensure heading has "Overview:" prefix
          let headingText = section.heading;
          if (!headingText.toLowerCase().startsWith('overview:')) {
            headingText = `Overview: ${headingText}`;
          }
          const headingLines = doc.splitTextToSize(headingText, maxWidth);
          headingLines.forEach((line, lineIdx) => {
            doc.text(line, margin, cursorY);
            cursorY += 14;
          });
          cursorY += 6;
        }

        // Render bullets
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11.5);
        doc.setTextColor('#000000');

        const bullets = section.bullets.length ? section.bullets : [];
        if (bullets.length > 0) {
          bullets.forEach((bullet) => {
            const bulletText = `• ${bullet}`;
            const wrapped = doc.splitTextToSize(bulletText, maxWidth - 18);
            wrapped.forEach((line, lineIdx) => {
              doc.text(line, margin + 18, cursorY);
              cursorY += 14;
            });
            cursorY += 4; // Space between bullets
          });
        }

        cursorY += 10; // Space between sections
      });

      cursorY += 18;

      let mindMapImageData = null;
      let snapshotWidth = 0;
      let snapshotHeight = 0;

      if (mindMapWrapperRef.current) {
        const reactFlowCanvas = mindMapWrapperRef.current.querySelector('.react-flow');
        if (reactFlowCanvas) {
          snapshotWidth = reactFlowCanvas.clientWidth;
          snapshotHeight = reactFlowCanvas.clientHeight;
          try {
            // Capture snapshot - errors from external stylesheets won't prevent PDF generation
            mindMapImageData = await toPng(reactFlowCanvas, {
              backgroundColor: '#f5f5f5',
              pixelRatio: 2,
              quality: 1
            }).catch(err => {
              // Ignore CORS and security errors, they don't prevent the snapshot
              if (err.message?.includes('CORS') || err.message?.includes('cssRules') || err.message?.includes('SecurityError')) {
                // Return a fallback or continue without snapshot
                return null;
              }
              throw err;
            });
          } catch (error) {
            // Continue without snapshot if it fails - PDF will still be generated with summary
            console.warn('Mind map snapshot skipped:', error.message);
            mindMapImageData = null;
          }
        }
      }

      if (mindMapImageData) {
        const imageWidth = pageWidth - margin * 2;
        const aspectRatio =
          snapshotWidth && snapshotHeight ? snapshotWidth / snapshotHeight : 1.6;
        const imageHeight = imageWidth / aspectRatio;
        ensureSpace(imageHeight + 40);
        doc.setFontSize(14);
        doc.setTextColor('#000000');
        doc.text('Mind Map Snapshot', margin, cursorY);
        cursorY += 16;
        doc.addImage(mindMapImageData, 'PNG', margin, cursorY, imageWidth, imageHeight);
        cursorY += imageHeight + 16;
      }

      const safeTitle = videoTitle.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
      doc.save(`${safeTitle || 'video-summary'}.pdf`);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
    } finally {
      setIsDownloadingSummary(false);
    }
  }, [summaryText, loadJsPDF, videoTitle, currentAnalysis, loadHtml2Canvas]);

  // Generate nodes and edges from mind map data
  const mindMapLayout = useMemo(() => {
    if (!currentAnalysis?.analysis?.mind_map) {
      return { nodes: [], edges: [], height: 720 };
    }

    const mindMap = currentAnalysis.analysis.mind_map;
    const nodes = [];
    const edges = [];
    const rawBranches = mindMap.branches || [];
    const removeTimestamp = (text = '') =>
      text
        .replace(/\[\d{1,2}:\d{2}(?::\d{2})?\s*(?:->|to)\s*\d{1,2}:\d{2}(?::\d{2})?\]/gi, '')
        .replace(/\(?\d{1,2}:\d{2}(?::\d{2})?\)?/g, '')
        .replace(/\s+/g, ' ')
        .trim();

    const branches = rawBranches.slice(0, 8).map(branch => ({
      ...branch,
      topic: removeTimestamp(branch.topic),
      key_points: (branch.key_points || []).map(point =>
        removeTimestamp(point.replace(/\[[^\]]*\]/g, '').replace(/^\s*[-•]\s*/, ''))
      )
    }));
    const branchSpacing = 280; // Increased spacing to prevent overlap
    const branchCount = Math.max(branches.length, 1);
    // Calculate height based on branches on one side (approx half)
    const sideCount = Math.ceil(branchCount / 2);
    const totalHeight = Math.max(800, sideCount * branchSpacing + 400);
    const centerY = totalHeight / 2;
    const centerX = 800; // Center of the canvas

    const thumbnail = currentAnalysis.thumbnail ||
      currentAnalysis.analysis?.thumbnail ||
      currentAnalysis.analysis?.video_metadata?.thumbnail ||
      currentAnalysis.analysis?.video_metadata?.thumbnailUrl ||
      '';

    const channelName =
      currentAnalysis.analysis?.video_metadata?.channel ||
      currentAnalysis.analysis?.video_metadata?.channelTitle ||
      currentAnalysis.analysis?.channel ||
      'Channel';

    nodes.push({
      id: 'central',
      type: 'central', // Use custom central node
      position: { x: centerX - 160, y: centerY - 50 }, // Center the node (width ~320)
      data: {
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 20px' }}>
            {thumbnail ? (
              <img
                src={thumbnail}
                alt={videoTitle}
                style={{ width: '90px', height: '70px', objectFit: 'cover', borderRadius: '10px', border: '1px solid #000000' }}
              />
            ) : null}
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: '1rem', fontWeight: '700', color: '#000000', marginBottom: '4px' }}>
                {videoTitle}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#4b5563' }}>{channelName}</div>
              {mindMap.central_topic && (
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
                  {mindMap.central_topic}
                </div>
              )}
            </div>
          </div>
        )
      },
      style: {
        background: '#ffffff',
        border: '2px solid #000000',
        borderRadius: '18px',
        padding: 0,
        minWidth: '260px',
        maxWidth: '320px',
        boxShadow: 'none'
      }
    });

    // Split branches into left and right
    branches.forEach((branch, idx) => {
      const isLeft = idx % 2 === 0; // Even indices on Left, Odd on Right
      const sideIdx = Math.floor(idx / 2);

      // Calculate Y position relative to center
      // Distribute from top to bottom
      const offsetY = (sideIdx - (sideCount - 1) / 2) * branchSpacing;
      const branchY = centerY + offsetY;

      const branchId = `branch-${idx}`;
      const branchX = isLeft ? centerX - 500 : centerX + 200;

      nodes.push({
        id: branchId,
        type: isLeft ? 'left' : 'right',
        position: { x: branchX, y: branchY },
        data: {
          label: (
            <div style={{ padding: '14px 20px', textAlign: 'left' }}>
              <div style={{ fontSize: '1rem', fontWeight: '700', color: '#000000', marginBottom: '8px' }}>
                {branch.topic}
              </div>
              {branch.theory && (
                <div style={{ fontSize: '0.78rem', color: '#1f2937', lineHeight: '1.45' }}>
                  {branch.theory}
                </div>
              )}
            </div>
          )
        },
        style: {
          background: '#ffffff',
          border: '2px solid #000000',
          borderRadius: '14px',
          padding: 0,
          minWidth: '240px', // Slightly more concise
          maxWidth: '300px',
          boxShadow: 'none'
        }
      });

      edges.push({
        id: `edge-central-${branchId}`,
        source: 'central',
        target: branchId,
        sourceHandle: isLeft ? 'left' : 'right', // Connect to appropriate handle
        type: 'smoothstep', // Use smoothstep for cleaner lines
        style: { stroke: '#000000', strokeWidth: 2 }
      });

      branch.key_points.slice(0, 3).forEach((point, pointIdx) => {
        const pointId = `point-${idx}-${pointIdx}`;
        // Stack key points vertically below/near the branch
        const pointY = branchY + (pointIdx * 80) - 40;
        // Place key points further out
        const pointX = isLeft ? branchX - 320 : branchX + 340;

        nodes.push({
          id: pointId,
          type: isLeft ? 'leftLeaf' : 'rightLeaf',
          position: { x: pointX, y: pointY },
          data: {
            label: (
              <div style={{ padding: '10px 14px', fontSize: '0.78rem', color: '#000000', textAlign: 'left', lineHeight: '1.4' }}>
                {point}
              </div>
            )
          },
          style: {
            background: '#ffffff',
            border: '1px solid #000000',
            borderRadius: '10px',
            padding: 0,
            minWidth: '180px',
            maxWidth: '260px'
          }
        });

        edges.push({
          id: `edge-${branchId}-${pointId}`,
          source: branchId,
          target: pointId,
          type: 'smoothstep',
          style: { stroke: '#000000', strokeWidth: 1.5 }
        });
      });
    });

    return { nodes, edges, height: totalHeight };
  }, [currentAnalysis, videoTitle]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const flowHeight = Math.max(650, mindMapLayout.height || 720);

  // Reset visible nodes when layout changes
  useEffect(() => {
    if (mindMapLayout.nodes.length > 0) {
      setVisibleNodeCount(0);
    }
  }, [mindMapLayout]);

  // Step-by-step animation
  useEffect(() => {
    if (mindMapLayout.nodes.length === 0) return;

    if (visibleNodeCount < mindMapLayout.nodes.length) {
      const timer = setTimeout(() => {
        setVisibleNodeCount(prev => prev + 1);
      }, 300); // 300ms delay between nodes
      return () => clearTimeout(timer);
    }
  }, [visibleNodeCount, mindMapLayout.nodes.length]);

  // Update nodes and edges based on visibility
  useEffect(() => {
    const visibleNodes = mindMapLayout.nodes.slice(0, visibleNodeCount);
    // Find edges where both source and target are visible
    const visibleNodeIds = new Set(visibleNodes.map(n => n.id));
    const visibleEdges = mindMapLayout.edges.filter(
      e => visibleNodeIds.has(e.source) && visibleNodeIds.has(e.target)
    );

    setNodes(visibleNodes);
    setEdges(visibleEdges);
  }, [visibleNodeCount, mindMapLayout, setNodes, setEdges]);

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  const handleGenerateMindMap = async () => {
    const url = buildYoutubeUrlFromAnalysis(currentAnalysis);
    if (!url) {
      setManualStatus({
        error: 'Unable to detect the original video URL. Paste the link below to generate a mind map manually.',
        success: ''
      });
      return;
    }
    await generateMindMapFromUrl(url, currentAnalysis?.id || 'analysis');
  };

  const handleManualMindMapGenerate = async () => {
    if (!manualUrl.trim()) {
      setManualStatus({ error: 'Please paste a YouTube video link first.', success: '' });
      return;
    }
    await generateMindMapFromUrl(manualUrl.trim(), 'manual');
  };

  if (availableAnalyses.length === 0) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 20px 40px',
        background: 'linear-gradient(135deg, #0f0f1e 0%, #1a1a2e 100%)'
      }}>
        <div style={{
          textAlign: 'center',
          color: '#d1d5db',
          maxWidth: '600px',
          width: '100%'
        }}>
          <h2 style={{
            fontSize: '2rem',
            marginBottom: '1.5rem',
            background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>
            Analyze a video to see its mind map
          </h2>

          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            padding: '2rem',
            borderRadius: '20px',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            backdropFilter: 'blur(10px)'
          }}>
            <p style={{ marginBottom: '1.5rem', color: '#9ca3af' }}>
              Enter a YouTube URL below to generate a visual knowledge graph instantly.
            </p>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '1rem' }}>
              <input
                type="text"
                value={manualUrl}
                onChange={(e) => setManualUrl(e.target.value)}
                placeholder="Paste YouTube URL here..."
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  background: 'rgba(0, 0, 0, 0.3)',
                  color: 'white',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleManualMindMapGenerate()}
              />
              <button
                onClick={handleManualMindMapGenerate}
                disabled={isGeneratingMindMap}
                style={{
                  padding: '12px 24px',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                  color: 'white',
                  fontWeight: '600',
                  cursor: isGeneratingMindMap ? 'not-allowed' : 'pointer',
                  opacity: isGeneratingMindMap ? 0.7 : 1,
                  transition: 'transform 0.2s'
                }}
              >
                {isGeneratingMindMap ? 'Generating...' : 'Analyze'}
              </button>
            </div>

            {manualStatus.error && (
              <div style={{ color: '#ef4444', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                {manualStatus.error}
              </div>
            )}
            {manualStatus.success && (
              <div style={{ color: '#10b981', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                {manualStatus.success}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }



  if (!currentAnalysis?.analysis?.mind_map) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 20px 40px',
        background: 'linear-gradient(135deg, #0f0f1e 0%, #1a1a2e 100%)'
      }}>
        <div style={{
          textAlign: 'center',
          color: '#d1d5db',
          maxWidth: '600px'
        }}>
          <div style={{
            fontSize: '3rem',
            marginBottom: '1rem'
          }}>🗺️</div>
          <h2 style={{
            fontSize: '1.8rem',
            fontWeight: '700',
            color: '#e5e7eb',
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Generate Mind Map
          </h2>
          <p style={{
            fontSize: '1.1rem',
            color: '#9ca3af',
            marginBottom: '2rem',
            lineHeight: '1.6'
          }}>
            Create a visual mind map and timestamped summary for this video analysis
          </p>
          <button
            onClick={handleGenerateMindMap}
            disabled={isGeneratingMindMap}
            style={{
              padding: '1rem 2.5rem',
              fontSize: '1.1rem',
              fontWeight: '600',
              background: isGeneratingMindMap
                ? 'rgba(168, 85, 247, 0.5)'
                : 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
              border: 'none',
              borderRadius: '12px',
              color: 'white',
              cursor: isGeneratingMindMap ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: isGeneratingMindMap
                ? 'none'
                : '0 10px 25px rgba(168, 85, 247, 0.3)',
              opacity: isGeneratingMindMap ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (!isGeneratingMindMap) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 15px 35px rgba(168, 85, 247, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isGeneratingMindMap) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 10px 25px rgba(168, 85, 247, 0.3)';
              }
            }}
          >
            {isGeneratingMindMap ? (
              <>
                <span style={{ display: 'inline-block', marginRight: '0.5rem' }}>
                  <span style={{
                    display: 'inline-block',
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: 'white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                </span>
                Generating...
              </>
            ) : (
              'Generate Mind Map & Summary'
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', width: '100%', position: 'relative', background: '#f5f5f5' }}>

      <div style={{
        position: 'absolute',
        top: '70px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'min(1700px, 96vw)',
        zIndex: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem'
      }}>
        {availableAnalyses.length > 1 && (
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <label style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Previous Analyses</label>
            <select
              value={selectedAnalysisId || ''}
              onChange={handleAnalysisChange}
              style={{
                minWidth: '220px',
                padding: '10px 14px',
                fontSize: '0.95rem',
                background: 'rgba(15, 15, 35, 0.95)',
                border: '2px solid rgba(139, 92, 246, 0.5)',
                borderRadius: '12px',
                color: '#e5e7eb',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              {availableAnalyses.map((analysis) => (
                <option
                  key={analysis.id}
                  value={analysis.id}
                  style={{
                    background: '#0f0f23',
                    color: '#e5e7eb'
                  }}
                >
                  {analysis.title}
                </option>
              ))}
            </select>
          </div>
        )}
        <div style={{
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <input
            type="text"
            value={manualUrl}
            onChange={(e) => {
              setManualUrl(e.target.value);
              if (manualStatus.error || manualStatus.success) {
                setManualStatus({ error: '', success: '' });
              }
            }}
            placeholder="Paste a YouTube link to generate a fresh Visual Map"
            style={{
              flex: '1 1 320px',
              minWidth: '280px',
              padding: '10px 14px',
              fontSize: '0.95rem',
              borderRadius: '12px',
              border: '2px solid rgba(139, 92, 246, 0.4)',
              background: '#ffffff',
              color: '#111827',
              outline: 'none'
            }}
          />
          <button
            onClick={handleManualMindMapGenerate}
            disabled={!manualUrl.trim() || isGeneratingMindMap}
            style={{
              padding: '10px 18px',
              borderRadius: '12px',
              border: 'none',
              background: manualUrl.trim() && !isGeneratingMindMap ? 'linear-gradient(135deg, #a855f7, #7c3aed)' : 'rgba(168, 85, 247, 0.4)',
              color: '#ffffff',
              fontWeight: '600',
              cursor: manualUrl.trim() && !isGeneratingMindMap ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease'
            }}
          >
            {isGeneratingMindMap ? 'Generating…' : 'Generate from Link'}
          </button>
          {currentAnalysis?.analysis?.mind_map && (
            <button
              onClick={handleGenerateMindMap}
              disabled={isGeneratingMindMap}
              style={{
                padding: '10px 18px',
                borderRadius: '12px',
                border: '1px solid rgba(15, 15, 35, 0.4)',
                background: '#ffffff',
                color: '#111827',
                fontWeight: '600',
                cursor: isGeneratingMindMap ? 'not-allowed' : 'pointer'
              }}
            >
              Refresh Current Map
            </button>
          )}
        </div>
        {(manualStatus.error || manualStatus.success) && (
          <p style={{
            color: manualStatus.error ? '#f87171' : '#34d399',
            fontSize: '0.85rem'
          }}>
            {manualStatus.error || manualStatus.success}
          </p>
        )}
      </div>

      <div style={{
        position: 'absolute',
        top: '190px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'min(1700px, 96vw)',
        display: 'grid',
        gridTemplateColumns: summaryText ? 'minmax(900px, 1fr) 420px' : '1fr',
        gap: '1.25rem',
        alignItems: 'stretch',
        paddingBottom: '32px'
      }}>
        <div
          ref={mindMapWrapperRef}
          style={{
            minHeight: `${flowHeight}px`,
            height: `${flowHeight}px`,
            background: 'transparent',
            border: 'none',
            borderRadius: '0',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
            attributionPosition="bottom-right"
            minZoom={0.1}
            maxZoom={1.5}
            defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
          >
            <Background color="#aaa" gap={16} />
            <Controls />
          </ReactFlow>

          {selectedNode && selectedNode.id !== 'central' && (
            <div style={{
              position: 'absolute',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(15, 15, 35, 0.95)',
              border: '2px solid rgba(139, 92, 246, 0.5)',
              borderRadius: '16px',
              padding: '1.5rem',
              maxWidth: '420px',
              width: '90%',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              zIndex: 10
            }}>
              <button
                onClick={() => setSelectedNode(null)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: 'none',
                  border: 'none',
                  color: '#9ca3af',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '5px'
                }}
              >
                ×
              </button>
              <h3 style={{
                color: '#8b5cf6',
                fontSize: '1.2rem',
                marginBottom: '0.75rem'
              }}>
                {selectedNode.data?.labelText || 'Details'}
              </h3>
              <div style={{ color: '#d1d5db', fontSize: '0.95rem', lineHeight: 1.6 }}>
                {selectedNode.data?.label || 'Additional context not available.'}
              </div>
            </div>
          )}
        </div>

        {summaryText && (
          <div style={{
            maxHeight: `${flowHeight}px`,
            background: 'rgba(15, 15, 35, 0.95)',
            border: '2px solid rgba(139, 92, 246, 0.35)',
            borderRadius: '24px',
            padding: '1.35rem 1.5rem',
            boxShadow: '0 18px 45px rgba(0,0,0,0.4)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <div>
                <p style={{ color: '#9ca3af', fontSize: '0.8rem', letterSpacing: '0.07em', textTransform: 'uppercase' }}>
                  Notes
                </p>
                <h2 style={{ color: '#e5e7eb', fontSize: '1.4rem', marginTop: '0.2rem' }}>
                  Video Summary
                </h2>
              </div>
              <button
                onClick={handleDownloadSummary}
                disabled={isDownloadingSummary}
                style={{
                  background: '#ffffff',
                  border: '2px solid #000000',
                  borderRadius: '12px',
                  padding: '0.45rem 0.85rem',
                  color: '#000000',
                  fontWeight: '700',
                  cursor: isDownloadingSummary ? 'not-allowed' : 'pointer',
                  opacity: isDownloadingSummary ? 0.7 : 1,
                  boxShadow: 'none',
                  fontSize: '0.9rem'
                }}
              >
                {isDownloadingSummary ? 'Preparing…' : 'Download PDF'}
              </button>
            </div>

            <div style={{ overflowY: 'auto', paddingRight: '0.4rem' }}>
              {notesSections.length > 0 ? (
                <div style={{
                  background: 'rgba(15, 15, 35, 0.95)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '16px',
                  padding: '1.25rem 1.5rem',
                  color: '#e5e7eb'
                }}>
                  {notesSections.map((section, idx) => (
                    <div key={`${section.timestamp}-${idx}`} style={{ marginBottom: idx < notesSections.length - 1 ? '1.5rem' : '0' }}>
                      <p style={{ color: '#fbbf24', fontSize: '0.85rem', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '0.4rem' }}>
                        {section.timestamp || 'Overview'}
                      </p>
                      {section.title && (
                        <h3 style={{ color: '#ffffff', fontSize: '1rem', fontWeight: '600', margin: '0 0 0.35rem 0', lineHeight: 1.4 }}>
                          {section.title}
                        </h3>
                      )}
                      {section.theory && (
                        <p style={{ color: '#c7d2fe', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: section.bullets.length ? '0.5rem' : 0 }}>
                          {section.theory}
                        </p>
                      )}
                      {section.bullets.length > 0 && (
                        <ul style={{ color: '#e5e7eb', fontSize: '0.9rem', lineHeight: 1.7, marginLeft: '1rem', paddingLeft: '0.5rem' }}>
                          {section.bullets.map((bullet, bulletIdx) => (
                            bullet ? (
                              <li key={bulletIdx} style={{ marginBottom: '0.45rem' }}>
                                {bullet}
                              </li>
                            ) : null
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  background: 'rgba(15, 15, 35, 0.95)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '16px',
                  padding: '1.25rem 1.5rem',
                  color: '#e5e7eb',
                  fontSize: '0.95rem',
                  lineHeight: 1.8,
                  whiteSpace: 'pre-wrap'
                }}>
                  <p style={{ color: '#fbbf24', fontSize: '0.85rem', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '0.75rem' }}>
                    Overview
                  </p>
                  {summaryText || 'No summary available'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div >
  );
};

export default MindMapViewer;
